import { useEffect, useRef } from "react";
import { type DialogProps } from "@mui/material";
import { StorageScope } from "../../../../../services/api/instances/storageApi/types/service_types";
import { ModalBasic } from "../../../../../shared/components/ModalBasic";
import { useObjectManager } from "../../hook/use-object-manager";
import { AppNewInput } from "../../../../../shared/components/AppNewInput";
import { TModalsIds } from "../../../../../app/state/modalSlice/types";
import { useModalSelectors } from "../../../../../app/state/modalSlice/hook/use-modal-selectors";
import styles from "./styles.module.css";
import { useTranslation } from "react-i18next";

interface Props extends Omit<DialogProps, "open"> {
  service: StorageScope;
  closeModal: (id: TModalsIds, subtype?: string) => void;
  defaultName: string | undefined;
  addFolder?: (name: string) => void;
}

export const ObjectManagerModal = ({
  service,
  closeModal,
  defaultName,
  addFolder,
  ...rest
}: Props) => {
  const objectManager = useModalSelectors("object_manager");
  const {
    name,
    title,
    onOkButtonTitle,
    handleOkAction,
    handleCloseModal,
    handleKeyDown,
    handleChangeFolderName,
  } = useObjectManager(
    service,
    defaultName,
    objectManager,
    closeModal,
    addFolder,
  );
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (objectManager.isOpen) {
      const timer = setTimeout(() => {
        const input = inputRef.current;
        if (input) {
          input.focus();
          const end = input.value.length;
          input.setSelectionRange(end, end);
          input.select();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [objectManager.isOpen]);

  if (!objectManager.isOpen) return null;

  return (
    <ModalBasic
      open={objectManager.isOpen}
      title={title}
      hasCloseButton
      closeModal={handleCloseModal}
      onClose={handleCloseModal}
      hasFooter
      buttonOkText={onOkButtonTitle}
      onOk={handleOkAction}
      onCancel={handleCloseModal}
      disabledOkBtn={name === ""}
      {...rest}
      className={styles["modal"]}
    >
      <div className={styles.container}>
        <AppNewInput
          value={name}
          ref={inputRef}
          className={styles.input}
          onKeyDown={handleKeyDown}
          onChange={handleChangeFolderName}
          placeholder={t("common.input_name_enter_placeholder")}
        />
      </div>
    </ModalBasic>
  );
};
